import { useEffect, useState, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import ConfirmDialog from "components/ConfirmDialog";
import { formatDate } from "helpers/formatDate";
import { formatCurrency } from "helpers/utils";
import type { IPagamentoLoja } from "interfaces/loja";
import { LojaPageContainer } from "../../common";
import {
  detalharPagamentoGerenciarLoja,
  atualizarStatusPagamentoLoja,
} from "../../../../../services/lojaPagamentos";
import { useMaterialUIController, setMessage } from "context";
import {
  chipColorStatusPagamento,
  labelMetodoPagamentoLoja,
  labelStatusPagamentoLoja,
  resumoIdPagamento,
  textoParcelasCartao,
} from "utils/pagamentoLojaUi";
import { colors } from "@mui/material";

function Linha({
  rotulo,
  valor,
  monospace,
}: {
  rotulo: string;
  valor: ReactNode;
  monospace?: boolean;
}) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        fontWeight={600}
      >
        {rotulo}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        sx={
          monospace
            ? { fontFamily: "ui-monospace, monospace", wordBreak: "break-all" }
            : undefined
        }
      >
        {valor}
      </Typography>
    </Grid>
  );
}

export default function LojaPagamentoDetalhe() {
  const { pagamentoId } = useParams();
  const navigate = useNavigate();
  const [, dispatch] = useMaterialUIController();
  const [pg, setPg] = useState<IPagamentoLoja | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDlg, setConfirmDlg] = useState<{
    open: boolean;
    action?: "PAGO" | "CANCELADO" | "ESTORNADO";
  }>({ open: false });
  const [boletoDlg, setBoletoDlg] = useState<{ open: boolean; motivo: string }>(
    {
      open: false,
      motivo: "",
    }
  );

  useEffect(() => {
    (async () => {
      setError(null);
      if (!pagamentoId) {
        setPg(null);
        setError("URL sem ID do pagamento.");
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const { data, status } =
          await detalharPagamentoGerenciarLoja(pagamentoId);
        if (
          status === 200 &&
          data &&
          typeof data === "object" &&
          "id" in data
        ) {
          setPg(data as IPagamentoLoja);
        } else {
          setPg(null);
          setError("Resposta inválida da API.");
        }
      } catch (e: unknown) {
        setPg(null);
        const msg =
          e instanceof Error
            ? e.message
            : "Não foi possível carregar o pagamento.";
        setError(msg);
        setMessage(dispatch, { open: true, severity: "error", text: msg });
      } finally {
        setLoading(false);
      }
    })();
  }, [pagamentoId, dispatch]);

  async function aplicarStatus(
    status: "PAGO" | "CANCELADO" | "ESTORNADO",
    extras?: { motivoBaixaManual?: string }
  ) {
    if (!pagamentoId) return;
    setSaving(true);
    try {
      await atualizarStatusPagamentoLoja(pagamentoId, status, {
        motivoBaixaManual: extras?.motivoBaixaManual,
      });
      const { data, status: st } =
        await detalharPagamentoGerenciarLoja(pagamentoId);
      if (st === 200 && data) setPg(data as IPagamentoLoja);
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: `Status atualizado para ${status}.`,
      });
      if (extras?.motivoBaixaManual) {
        setBoletoDlg({ open: false, motivo: "" });
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Falha ao atualizar status.";
      setMessage(dispatch, { open: true, severity: "error", text: msg });
    } finally {
      setSaving(false);
      setConfirmDlg({ open: false });
    }
  }

  const disablePagar =
    !pg || saving || ["PAGO", "CANCELADO", "ESTORNADO"].includes(pg.status);
  const disableEstornar =
    !pg || saving || ["ESTORNADO", "CANCELADO"].includes(pg.status);
  const disableCancelar =
    !pg || saving || ["CANCELADO", "ESTORNADO"].includes(pg.status);

  return (
    <LojaPageContainer
      title="Pagamento"
      subtitle={pagamentoId ? `ID interno · ${pagamentoId}` : undefined}
      actions={
        <MDBox display="flex" gap={1} flexWrap="wrap">
          <MDButton
            variant="outlined"
            color="dark"
            onClick={() => navigate(-1)}
          >
            Voltar
          </MDButton>
          {pg?.pedidoId ? (
            <MDButton
              variant="outlined"
              color="info"
              onClick={() => navigate(`/loja/pedidos/${pg.pedidoId}`)}
            >
              Abrir pedido
            </MDButton>
          ) : null}
        </MDBox>
      }
    >
      {loading ? (
        <MDBox display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </MDBox>
      ) : error ? (
        <MDTypography variant="button" color="error">
          {error}
        </MDTypography>
      ) : pg ? (
        <Stack spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="h5" fontWeight={700}>
              {formatCurrency(Number(pg.valor))}
            </Typography>
            <Chip
              label={labelStatusPagamentoLoja(pg.status)}
              color={chipColorStatusPagamento(pg.status)}
              variant={pg.status === "PAGO" ? "filled" : "outlined"}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {labelMetodoPagamentoLoja(pg.metodo)}
            {pg.moeda ? ` · ${pg.moeda}` : ""}
          </Typography>

          <Divider />

          <Grid container spacing={2}>
            {(() => {
              const parcelasLabel = textoParcelasCartao(
                pg.metodo,
                pg.parcelas ?? null
              );
              return parcelasLabel ? (
                <Linha rotulo="Parcelamento" valor={parcelasLabel} />
              ) : null;
            })()}
            {pg.cartaoFinal?.trim() || pg.cardMask?.trim() ? (
              <Linha
                rotulo="Cartão"
                valor={(pg.cartaoFinal ?? pg.cardMask ?? "").trim()}
                monospace
              />
            ) : null}
            {pg.chargeId?.trim() ? (
              <Linha
                rotulo="ID da cobrança (provedor)"
                valor={pg.chargeId.trim()}
                monospace
              />
            ) : null}
            {pg.txid?.trim() ? (
              <Linha rotulo="TXID (PIX)" valor={pg.txid.trim()} monospace />
            ) : null}
            {pg.provedor?.trim() ? (
              <Linha rotulo="Provedor" valor={pg.provedor.trim()} />
            ) : null}
            <Linha
              rotulo="ID do pagamento"
              valor={
                <span title={resumoIdPagamento(pg.id).completo}>
                  {resumoIdPagamento(pg.id).curto}
                </span>
              }
              monospace
            />
            <Linha rotulo="Pedido" valor={pg.pedidoId} monospace />
            {pg.criadoEm ? (
              <Linha
                rotulo="Registrado em"
                valor={formatDate(pg.criadoEm, "dd/MM/yyyy HH:mm:ss")}
              />
            ) : null}
            {pg.pagoEm ? (
              <Linha
                rotulo="Pago em"
                valor={formatDate(pg.pagoEm, "dd/MM/yyyy HH:mm:ss")}
              />
            ) : null}
            {pg.canceladoEm ? (
              <Linha
                rotulo="Cancelado em"
                valor={formatDate(pg.canceladoEm, "dd/MM/yyyy HH:mm:ss")}
              />
            ) : null}
            {pg.estornadoEm ? (
              <Linha
                rotulo="Estornado em"
                valor={formatDate(pg.estornadoEm, "dd/MM/yyyy HH:mm:ss")}
              />
            ) : null}
          </Grid>

          {pg.respostaBrutaTexto?.trim() ? (
            <Accordion
              disableGutters
              elevation={0}
              sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: "#767676", color: "#ffffff" }}
              >
                <Typography variant="button" fontWeight={600}>
                  Resposta bruta (provedor)
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{ backgroundColor: "#333333", color: "#ffffff" }}
              >
                <Typography
                  component="pre"
                  variant="caption"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "ui-monospace, monospace",
                    maxHeight: 360,
                    overflow: "auto",
                    m: 0,
                  }}
                >
                  {JSON.stringify(
                    JSON.parse(pg.respostaBrutaTexto.trim()),
                    null,
                    2
                  )}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ) : null}

          {pg.observacaoInterna?.trim() ? (
            <Accordion
              disableGutters
              elevation={0}
              sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="button" fontWeight={600}>
                  Observação interna (auditoria)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  component="pre"
                  variant="caption"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "ui-monospace, monospace",
                    m: 0,
                  }}
                >
                  {pg.observacaoInterna.trim()}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ) : null}

          <Divider />

          <Stack direction="row" flexWrap="wrap" gap={1}>
            <MDButton
              size="small"
              variant="gradient"
              color="dark"
              disabled={disablePagar}
              onClick={() => {
                if (pg.metodo === "BOLETO") {
                  setBoletoDlg({ open: true, motivo: "" });
                } else {
                  setConfirmDlg({ open: true, action: "PAGO" });
                }
              }}
            >
              Marcar pago
            </MDButton>
            <MDButton
              size="small"
              color="warning"
              disabled={disableEstornar}
              onClick={() => setConfirmDlg({ open: true, action: "ESTORNADO" })}
            >
              Estornar
            </MDButton>
            <MDButton
              size="small"
              color="error"
              disabled={disableCancelar}
              onClick={() => setConfirmDlg({ open: true, action: "CANCELADO" })}
            >
              Cancelar
            </MDButton>
          </Stack>
        </Stack>
      ) : null}

      <ConfirmDialog
        open={confirmDlg.open}
        title="Confirmar alteração"
        onClose={() => setConfirmDlg({ open: false })}
        onConfirm={() => {
          if (confirmDlg.action) void aplicarStatus(confirmDlg.action);
        }}
      />

      <Dialog
        open={boletoDlg.open}
        onClose={() => {
          if (!saving) setBoletoDlg({ open: false, motivo: "" });
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Baixa manual do boleto</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A cobrança será liquidada na Efí (settle) e o pagamento marcado como
            pago. Descreva o motivo da baixa manual — o texto fica em{" "}
            <strong>observacaoInterna</strong> junto com o utilizador que
            executou a ação.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Motivo da baixa manual"
            value={boletoDlg.motivo}
            onChange={(e) =>
              setBoletoDlg((s) => ({ ...s, motivo: e.target.value }))
            }
            disabled={saving}
            placeholder="Ex.: Cliente pagou por TED; comprovante anexado ao pedido."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setBoletoDlg({ open: false, motivo: "" })}
            disabled={saving}
          >
            Cancelar
          </Button>
          <MDButton
            variant="contained"
            color="primary"
            disabled={saving || !boletoDlg.motivo.trim()}
            onClick={() => {
              const m = boletoDlg.motivo.trim();
              if (!m) return;
              void aplicarStatus("PAGO", { motivoBaixaManual: m });
            }}
          >
            Liquidar na Efí e marcar pago
          </MDButton>
        </DialogActions>
      </Dialog>
    </LojaPageContainer>
  );
}
